import { useState, useEffect } from 'react';
import { api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { formatDate } from '../lib/utils';
import { turkishIncludes } from '../lib/turkishSearch';
import { invalidateFirms } from '../lib/useFirms';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  Search,
  Globe,
  Download,
  Upload,
  Database,
  CheckCircle,
  AlertCircle,
  FileJson,
  Users,
  Calendar,
  FileText,
  Settings
} from 'lucide-react';

export const Firms = () => {
  const { t } = useTranslation();
  
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, firm: null });
  const [deleteDialog, setDeleteDialog] = useState({ 
    open: false, 
    firm: null, 
    step: 1,  // 1: first confirmation, 2: second confirmation with stats
    loading: false,
    stats: null
  });
  const [backupDialog, setBackupDialog] = useState({ open: false, firm: null, loading: false, data: null });
  const [restoreDialog, setRestoreDialog] = useState({ 
    open: false, 
    firm: null, 
    loading: false, 
    file: null,
    preview: null,
    options: {
      restore_users: true,
      restore_leave_requests: true,
      restore_daily_statuses: true,
      restore_balances: true,
      restore_documents: true,
      restore_settings: true,
      restore_notifications: false
    }
  });
  const [formData, setFormData] = useState({ 
    name: '', 
    logo_url: '',
    cname_domain: '',
    site_title: '',
    site_description: '',
    notification_email: '',
    allow_registration: true
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchFirms();
  }, []);

  const fetchFirms = async () => {
    setLoading(true);
    try {
      const response = await api.get('/firms/');
      if (response.data.success) {
        setFirms(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch firms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    setProcessing(true);
    
    try {
      await api.post('/firms/', formData);
      toast.success(t('common.success'));
      setCreateDialog(false);
      setFormData({ 
        name: '', 
        logo_url: '',
        cname_domain: '',
        site_title: '',
        site_description: '',
        notification_email: '',
        allow_registration: true
      });
      fetchFirms();
      invalidateFirms(); // cross-tab: Users/SendNotification gibi sayfalar anında güncellensin
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create firm');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim() || !editDialog.firm) return;
    setProcessing(true);
    
    try {
      await api.put(`/firms/${editDialog.firm.id}`, formData);
      toast.success(t('common.success'));
      setEditDialog({ open: false, firm: null });
      setFormData({ 
        name: '', 
        logo_url: '',
        cname_domain: '',
        site_title: '',
        site_description: '',
        notification_email: '',
        allow_registration: true
      });
      fetchFirms();
      invalidateFirms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update firm');
    } finally {
      setProcessing(false);
    }
  };

  // Read a chosen file as a data: URL and store directly into formData.logo_url.
  // We accept up to ~512KB so small PNG/SVG/JPEG marks fit; larger files prompt
  // the user to use a hosted URL instead (avoids ballooning the firm document).
  const handleLogoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir resim dosyası seçin (PNG, JPG, SVG)');
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error('Logo en fazla 512 KB olmalı. Daha büyük dosyalar için URL kullanın.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFormData((p) => ({ ...p, logo_url: reader.result }));
    reader.onerror = () => toast.error('Dosya okunamadı');
    reader.readAsDataURL(file);
  };

  const handleDelete = async () => {
    if (!deleteDialog.firm) return;
    setProcessing(true);
    
    try {
      await api.delete(`/firms/${deleteDialog.firm.id}`);
      toast.success(t('common.success'));
      setDeleteDialog({ open: false, firm: null, step: 1, loading: false, stats: null });
      fetchFirms();
      invalidateFirms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete firm');
    } finally {
      setProcessing(false);
    }
  };

  // Open delete dialog with step 1
  const openDeleteDialog = async (firm) => {
    setDeleteDialog({ open: true, firm, step: 1, loading: true, stats: null });
    
    try {
      const response = await api.get(`/firms/${firm.id}/stats`);
      if (response.data.success) {
        setDeleteDialog(prev => ({ ...prev, loading: false, stats: response.data.data }));
      }
    } catch (error) {
      setDeleteDialog(prev => ({ ...prev, loading: false, stats: null }));
    }
  };

  // Proceed to step 2 (second confirmation)
  const proceedToStep2 = () => {
    setDeleteDialog(prev => ({ ...prev, step: 2 }));
  };

  // Force delete firm with all data
  const handleForceDelete = async () => {
    if (!deleteDialog.firm) return;
    setProcessing(true);
    
    try {
      const response = await api.delete(`/firms/${deleteDialog.firm.id}/force`);
      if (response.data.success) {
        const stats = response.data.data;
        toast.success(
          `"${stats.firm_name}" silindi: ${stats.users_deleted} kullanıcı, ${stats.leave_requests_deleted} izin talebi, ${stats.daily_statuses_deleted} çalışma durumu`
        );
        setDeleteDialog({ open: false, firm: null, step: 1, loading: false, stats: null });
        fetchFirms();
        invalidateFirms();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    } finally {
      setProcessing(false);
    }
  };

  // Backup firm data
  const handleBackup = async (firm) => {
    setBackupDialog({ open: true, firm, loading: true, data: null });
    
    try {
      const response = await api.get(`/firms/${firm.id}/backup`);
      if (response.data.success) {
        setBackupDialog(prev => ({ ...prev, loading: false, data: response.data.data }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yedekleme başarısız');
      setBackupDialog({ open: false, firm: null, loading: false, data: null });
    }
  };

  // Download backup as JSON file
  const downloadBackup = () => {
    if (!backupDialog.data) return;
    
    const dataStr = JSON.stringify(backupDialog.data, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `backup_${backupDialog.firm?.name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Yedek dosyası indirildi');
  };

  // Open restore dialog
  const openRestoreDialog = (firm) => {
    setRestoreDialog({ 
      open: true, 
      firm, 
      loading: false, 
      file: null,
      preview: null,
      options: {
        restore_users: true,
        restore_leave_requests: true,
        restore_daily_statuses: true,
        restore_balances: true,
        restore_documents: true,
        restore_settings: true,
        restore_notifications: false
      }
    });
  };

  // Handle file upload for restore
  const handleRestoreFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate backup format
      if (!data.backup_info || !data.firm) {
        toast.error('Geçersiz yedek dosyası formatı');
        return;
      }
      
      setRestoreDialog(prev => ({ 
        ...prev, 
        file,
        preview: data
      }));
    } catch (error) {
      toast.error('Dosya okunamadı veya geçersiz JSON formatı');
    }
  };

  // Execute restore
  const handleRestore = async () => {
    if (!restoreDialog.preview || !restoreDialog.firm) return;
    
    setRestoreDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await api.post(`/firms/${restoreDialog.firm.id}/restore`, {
        backup_data: restoreDialog.preview,
        restore_options: restoreDialog.options
      });
      
      if (response.data.success) {
        const results = response.data.data;
        toast.success(
          `Geri yükleme tamamlandı: ${results.users_restored} kullanıcı, ${results.leave_requests_restored} izin talebi, ${results.daily_statuses_restored} çalışma durumu`
        );
        setRestoreDialog({ open: false, firm: null, loading: false, file: null, preview: null, options: {} });
        fetchFirms();
        invalidateFirms();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geri yükleme başarısız');
    } finally {
      setRestoreDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Create firm from backup (import)
  const [importDialog, setImportDialog] = useState({ 
    open: false, 
    loading: false, 
    file: null,
    preview: null,
    newFirmName: ''
  });

  // Handle file upload for import
  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate backup format
      if (!data.backup_info || !data.firm) {
        toast.error('Geçersiz yedek dosyası formatı');
        return;
      }
      
      setImportDialog(prev => ({ 
        ...prev, 
        file,
        preview: data,
        newFirmName: data.firm?.name ? `${data.firm.name} (Import)` : ''
      }));
    } catch (error) {
      toast.error('Dosya okunamadı veya geçersiz JSON formatı');
    }
  };

  // Execute import (create firm from backup)
  const handleImport = async () => {
    if (!importDialog.preview) return;
    
    setImportDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await api.post('/firms/create-from-backup', {
        backup_data: importDialog.preview,
        new_firm_name: importDialog.newFirmName || null
      });
      
      if (response.data.success) {
        const results = response.data.data;
        toast.success(
          `Firma "${results.firm_name}" oluşturuldu: ${results.users_created} kullanıcı, ${results.leave_requests_created} izin talebi, ${results.daily_statuses_created} çalışma durumu`
        );
        setImportDialog({ open: false, loading: false, file: null, preview: null, newFirmName: '' });
        fetchFirms();
        invalidateFirms();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İçe aktarma başarısız');
    } finally {
      setImportDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const openEditDialog = (firm) => {
    setFormData({ 
      name: firm.name, 
      logo_url: firm.logo_url || '',
      cname_domain: firm.cname_domain || '',
      site_title: firm.site_title || '',
      site_description: firm.site_description || '',
      notification_email: firm.notification_email || '',
      allow_registration: firm.allow_registration !== false
    });
    setEditDialog({ open: true, firm });
  };

  const filteredFirms = firms.filter(f => 
    turkishIncludes(f.name || '', searchTerm) ||
    turkishIncludes(f.cname_domain || '', searchTerm)
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="firms-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('common.firms')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t('admin.firmManagement')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => setImportDialog({ open: true, loading: false, file: null, preview: null, newFirmName: '' })}
            className="gap-2"
            data-testid="import-firm-btn"
          >
            <Upload className="h-4 w-4" />
            Yedekten İçe Aktar
          </Button>
          <Button 
            onClick={() => setCreateDialog(true)}
            className="gap-2"
            data-testid="create-firm-btn"
          >
            <Plus className="h-4 w-4" />
            {t('admin.createFirm')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="firms-search-input"
          />
        </div>
      </div>

      {/* Firms Table */}
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : filteredFirms.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 dark:text-slate-400">{t('common.noData')}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.firmName')}</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Kayıt</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFirms.map((firm) => (
                <TableRow key={firm.id} data-testid={`firm-row-${firm.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                        {firm.logo_url ? (
                          <img src={firm.logo_url} alt={firm.name} className="w-8 h-8 rounded" />
                        ) : (
                          <Building2 className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <span className="font-medium block">{firm.name}</span>
                        {firm.site_title && (
                          <span className="text-xs text-slate-400">{firm.site_title}</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {firm.cname_domain ? (
                      <span className="font-mono text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        {firm.cname_domain}
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {firm.allow_registration !== false ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Açık</span>
                    ) : (
                      <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-1 rounded">Kapalı</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`status-badge-${(firm.status || 'active').toLowerCase()}`}>
                      {firm.status || 'ACTIVE'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(firm)}
                        data-testid={`edit-firm-btn-${firm.id}`}
                        title="Düzenle"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleBackup(firm)}
                        data-testid={`backup-firm-btn-${firm.id}`}
                        title="Yedekle"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        onClick={() => openRestoreDialog(firm)}
                        data-testid={`restore-firm-btn-${firm.id}`}
                        title="Geri Yükle"
                      >
                        <Upload className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDeleteDialog(firm)}
                        data-testid={`delete-firm-btn-${firm.id}`}
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('admin.createFirm')}</DialogTitle>
            <DialogDescription>Yeni firma oluştur ve domain ayarlarını yapılandır</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label>{t('admin.firmName')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Firma adını girin"
                data-testid="firm-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.firmLogo')}</Label>
              <div className="flex items-start gap-3">
                {formData.logo_url ? (
                  <img
                    src={formData.logo_url}
                    alt="logo"
                    className="h-14 w-14 rounded-lg object-cover border border-slate-200 dark:border-slate-700 shrink-0"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-14 w-14 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 text-slate-400">
                    <Building2 className="h-6 w-6" />
                  </div>
                )}
                <div className="flex-1 space-y-2 min-w-0">
                  <Input
                    value={formData.logo_url || ''}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    data-testid="firm-logo-input"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="firm-logo-file-create"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={(e) => handleLogoFile(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('firm-logo-file-create')?.click()}
                      data-testid="firm-logo-upload-btn"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" />
                      Dosya Yükle
                    </Button>
                    {formData.logo_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, logo_url: '' })}
                        className="text-rose-600 hover:text-rose-700"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Kaldır
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">PNG / JPG / SVG, en fazla 512 KB. URL de yapıştırabilirsiniz.</p>
                </div>
              </div>
            </div>

            {/* Domain Settings */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-slate-700 dark:text-slate-200">Domain Ayarları</span>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>CNAME / Domain URL</Label>
                  <Input
                    value={formData.cname_domain}
                    onChange={(e) => setFormData({ ...formData, cname_domain: e.target.value })}
                    placeholder="hr.firmaadiniz.com"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Bu firma için özel domain (DNS'te CNAME ayarı gerekir)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Bildirim E-postası</Label>
                  <Input
                    type="email"
                    value={formData.notification_email}
                    onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
                    placeholder="info@firmaadiniz.com"
                    data-testid="firm-notification-email"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    İş başvuruları, sistem bildirimleri ve operasyonel mailler buraya gider.
                    Virgülle birden fazla yazılabilir.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Site Başlığı (SEO)</Label>
                  <Input
                    value={formData.site_title}
                    onChange={(e) => setFormData({ ...formData, site_title: e.target.value })}
                    placeholder="Firma Adı - İK Portalı"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Site Açıklaması (SEO)</Label>
                  <Textarea
                    value={formData.site_description}
                    onChange={(e) => setFormData({ ...formData, site_description: e.target.value })}
                    placeholder="Firma açıklaması..."
                    rows={2}
                  />
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div>
                    <Label className="text-sm">Üyelik Kaydı Açık</Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Çalışanlar bu domain üzerinden kayıt olabilir</p>
                  </div>
                  <Switch
                    checked={formData.allow_registration}
                    onCheckedChange={(checked) => setFormData({ ...formData, allow_registration: checked })}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={processing || !formData.name.trim()}
              data-testid="confirm-create-firm-btn"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, firm: editDialog.firm })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('common.edit')} Firma</DialogTitle>
            <DialogDescription>Firma bilgilerini ve domain ayarlarını güncelle</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Basic Info */}
            <div className="space-y-2">
              <Label>{t('admin.firmName')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Firma adını girin"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.firmLogo')}</Label>
              <div className="flex items-start gap-3">
                {formData.logo_url ? (
                  <img
                    src={formData.logo_url}
                    alt="logo"
                    className="h-14 w-14 rounded-lg object-cover border border-slate-200 dark:border-slate-700 shrink-0"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-14 w-14 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 text-slate-400">
                    <Building2 className="h-6 w-6" />
                  </div>
                )}
                <div className="flex-1 space-y-2 min-w-0">
                  <Input
                    value={formData.logo_url || ''}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="firm-logo-file-edit"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={(e) => handleLogoFile(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('firm-logo-file-edit')?.click()}
                      data-testid="firm-logo-upload-btn-edit"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" />
                      Dosya Yükle
                    </Button>
                    {formData.logo_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, logo_url: '' })}
                        className="text-rose-600 hover:text-rose-700"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Kaldır
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">PNG / JPG / SVG, en fazla 512 KB. URL de yapıştırabilirsiniz.</p>
                </div>
              </div>
            </div>

            {/* Domain Settings */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-slate-700 dark:text-slate-200">Domain Ayarları</span>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>CNAME / Domain URL</Label>
                  <Input
                    value={formData.cname_domain}
                    onChange={(e) => setFormData({ ...formData, cname_domain: e.target.value })}
                    placeholder="hr.firmaadiniz.com"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Bu firma için özel domain (DNS'te CNAME ayarı gerekir)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Bildirim E-postası</Label>
                  <Input
                    type="email"
                    value={formData.notification_email}
                    onChange={(e) => setFormData({ ...formData, notification_email: e.target.value })}
                    placeholder="info@firmaadiniz.com"
                    data-testid="firm-notification-email"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    İş başvuruları, sistem bildirimleri ve operasyonel mailler buraya gider.
                    Virgülle birden fazla yazılabilir.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Site Başlığı (SEO)</Label>
                  <Input
                    value={formData.site_title}
                    onChange={(e) => setFormData({ ...formData, site_title: e.target.value })}
                    placeholder="Firma Adı - İK Portalı"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Site Açıklaması (SEO)</Label>
                  <Textarea
                    value={formData.site_description}
                    onChange={(e) => setFormData({ ...formData, site_description: e.target.value })}
                    placeholder="Firma açıklaması..."
                    rows={2}
                  />
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div>
                    <Label className="text-sm">Üyelik Kaydı Açık</Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Çalışanlar bu domain üzerinden kayıt olabilir</p>
                  </div>
                  <Switch
                    checked={formData.allow_registration}
                    onCheckedChange={(checked) => setFormData({ ...formData, allow_registration: checked })}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, firm: null })}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleUpdate} 
              disabled={processing || !formData.name.trim()}
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation - 2 Step Process */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, firm: null, step: 1, loading: false, stats: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {deleteDialog.step === 1 ? 'Firmayı Sil' : 'Son Onay - Geri Alınamaz!'}
            </DialogTitle>
            <DialogDescription>
              <strong>{deleteDialog.firm?.name}</strong> firmasını silmek üzeresiniz
            </DialogDescription>
          </DialogHeader>

          {deleteDialog.loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-3" />
              <p className="text-slate-500 dark:text-slate-400">Firma bilgileri yükleniyor...</p>
            </div>
          ) : deleteDialog.step === 1 ? (
            // Step 1: First confirmation with stats preview
            <div className="space-y-4">
              {deleteDialog.stats && deleteDialog.stats.users_count > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Bu firmada veri bulunuyor!</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Silme işlemi aşağıdaki tüm verileri kalıcı olarak silecektir.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              {deleteDialog.stats && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <Users className="h-4 w-4 mx-auto text-slate-500 dark:text-slate-400 mb-1" />
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{deleteDialog.stats.users_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Kullanıcı</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <Calendar className="h-4 w-4 mx-auto text-slate-500 dark:text-slate-400 mb-1" />
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{deleteDialog.stats.leave_requests_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">İzin Talebi</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <CheckCircle className="h-4 w-4 mx-auto text-slate-500 dark:text-slate-400 mb-1" />
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{deleteDialog.stats.daily_statuses_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Çalışma Durumu</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <FileText className="h-4 w-4 mx-auto text-slate-500 dark:text-slate-400 mb-1" />
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{deleteDialog.stats.leave_balances_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">İzin Bakiyesi</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <FileJson className="h-4 w-4 mx-auto text-slate-500 dark:text-slate-400 mb-1" />
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{deleteDialog.stats.documents_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Doküman</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <Settings className="h-4 w-4 mx-auto text-slate-500 dark:text-slate-400 mb-1" />
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{deleteDialog.stats.settings_count}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Ayar</p>
                  </div>
                </div>
              )}

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-start gap-2">
                <Download className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Öneri: Silmeden Önce Yedekleyin</p>
                  <p>Firmayı silmeden önce "Yedekle" butonunu kullanarak verileri dışa aktarabilirsiniz.</p>
                </div>
              </div>
            </div>
          ) : (
            // Step 2: Final confirmation
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">DİKKAT! Bu işlem geri alınamaz!</p>
                    <p className="text-sm text-red-700 mt-1">
                      <strong>{deleteDialog.firm?.name}</strong> firması ve tüm verileri (
                      {deleteDialog.stats?.users_count || 0} kullanıcı, 
                      {deleteDialog.stats?.leave_requests_count || 0} izin talebi,
                      {deleteDialog.stats?.daily_statuses_count || 0} çalışma durumu
                      ) kalıcı olarak silinecektir.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 border-2 border-red-300 border-dashed rounded-lg text-center">
                <p className="text-slate-600 dark:text-slate-300 mb-2">Silme işlemini onaylamak için:</p>
                <p className="text-lg font-mono font-bold text-red-600">"{deleteDialog.firm?.name}"</p>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">firmasının tüm verileri silinecektir.</p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, firm: null, step: 1, loading: false, stats: null })}
            >
              {t('common.cancel')}
            </Button>
            
            {deleteDialog.step === 1 ? (
              <Button 
                variant="destructive"
                onClick={proceedToStep2}
                disabled={deleteDialog.loading}
              >
                Devam Et
              </Button>
            ) : (
              <Button 
                variant="destructive"
                onClick={handleForceDelete}
                disabled={processing}
                className="bg-red-600 hover:bg-red-700"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Siliniyor...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Evet, Firmayı Sil
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Dialog */}
      <Dialog open={backupDialog.open} onOpenChange={(open) => !open && setBackupDialog({ open: false, firm: null, loading: false, data: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-600" />
              Firma Verilerini Yedekle
            </DialogTitle>
            <DialogDescription>
              <strong>{backupDialog.firm?.name}</strong> firmasına ait tüm veriler
            </DialogDescription>
          </DialogHeader>
          
          {backupDialog.loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
              <p className="text-slate-600 dark:text-slate-300">Veriler hazırlanıyor...</p>
            </div>
          ) : backupDialog.data ? (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Statistics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <Users className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                  <p className="text-2xl font-bold text-blue-700">{backupDialog.data.statistics?.users_count || 0}</p>
                  <p className="text-xs text-blue-600">Kullanıcı</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <Calendar className="h-5 w-5 mx-auto text-orange-600 mb-1" />
                  <p className="text-2xl font-bold text-orange-700">{backupDialog.data.statistics?.leave_requests_count || 0}</p>
                  <p className="text-xs text-orange-600">İzin Talebi</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <CheckCircle className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
                  <p className="text-2xl font-bold text-emerald-700">{backupDialog.data.statistics?.daily_statuses_count || 0}</p>
                  <p className="text-xs text-emerald-600">Çalışma Durumu</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <FileText className="h-5 w-5 mx-auto text-purple-600 mb-1" />
                  <p className="text-2xl font-bold text-purple-700">{backupDialog.data.statistics?.documents_count || 0}</p>
                  <p className="text-xs text-purple-600">Doküman</p>
                </div>
              </div>
              
              {/* Backup Info */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2">
                  <FileJson className="h-4 w-4" />
                  Yedek Bilgileri
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Oluşturulma:</span>
                    <span className="ml-2 text-slate-700 dark:text-slate-200">{new Date(backupDialog.data.backup_info?.created_at).toLocaleString('tr-TR')}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Versiyon:</span>
                    <span className="ml-2 text-slate-700 dark:text-slate-200">{backupDialog.data.backup_info?.version}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">İzin Bakiyeleri:</span>
                    <span className="ml-2 text-slate-700 dark:text-slate-200">{backupDialog.data.statistics?.leave_balances_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 dark:text-slate-400">Ayarlar:</span>
                    <span className="ml-2 text-slate-700 dark:text-slate-200">{backupDialog.data.statistics?.settings_count || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* Users Preview */}
              {backupDialog.data.users?.length > 0 && (
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2">Kullanıcılar ({backupDialog.data.users.length})</h4>
                  <div className="max-h-32 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {backupDialog.data.users.slice(0, 20).map(u => (
                        <span key={u.id} className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                          {u.full_name}
                        </span>
                      ))}
                      {backupDialog.data.users.length > 20 && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">+{backupDialog.data.users.length - 20} daha</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setBackupDialog({ open: false, firm: null, loading: false, data: null })}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={downloadBackup} 
              disabled={!backupDialog.data}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Download className="h-4 w-4 mr-2" />
              JSON İndir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreDialog.open} onOpenChange={(open) => !open && setRestoreDialog({ open: false, firm: null, loading: false, file: null, preview: null, options: {} })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-emerald-600" />
              Verileri Geri Yükle
            </DialogTitle>
            <DialogDescription>
              <strong>{restoreDialog.firm?.name}</strong> firmasına veri aktarın
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* File Upload */}
            <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg text-center">
              <input
                type="file"
                accept=".json"
                onChange={handleRestoreFileChange}
                className="hidden"
                id="restore-file-input"
              />
              <label htmlFor="restore-file-input" className="cursor-pointer">
                <FileJson className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                <p className="text-slate-600 dark:text-slate-300">Yedek dosyasını seçin (.json)</p>
                <p className="text-xs text-slate-400 mt-1">Dosyayı sürükleyin veya tıklayarak seçin</p>
              </label>
              {restoreDialog.file && (
                <div className="mt-3 p-2 bg-emerald-50 rounded text-emerald-700 text-sm">
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  {restoreDialog.file.name}
                </div>
              )}
            </div>
            
            {/* Preview */}
            {restoreDialog.preview && (
              <>
                {/* Backup Source Info */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Yedek Kaynağı</p>
                      <p className="text-sm text-amber-700">
                        Firma: <strong>{restoreDialog.preview.backup_info?.firm_name}</strong>
                      </p>
                      <p className="text-sm text-amber-700">
                        Tarih: {new Date(restoreDialog.preview.backup_info?.created_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Statistics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{restoreDialog.preview.statistics?.users_count || 0}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Kullanıcı</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{restoreDialog.preview.statistics?.leave_requests_count || 0}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">İzin Talebi</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{restoreDialog.preview.statistics?.daily_statuses_count || 0}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Çalışma Durumu</p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                    <p className="text-xl font-bold text-slate-700 dark:text-slate-200">{restoreDialog.preview.statistics?.documents_count || 0}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Doküman</p>
                  </div>
                </div>
                
                {/* Restore Options */}
                <div className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-medium text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Geri Yükleme Seçenekleri
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch 
                        checked={restoreDialog.options.restore_users}
                        onCheckedChange={(v) => setRestoreDialog(prev => ({
                          ...prev,
                          options: { ...prev.options, restore_users: v }
                        }))}
                      />
                      <span className="text-sm">Kullanıcılar</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch 
                        checked={restoreDialog.options.restore_leave_requests}
                        onCheckedChange={(v) => setRestoreDialog(prev => ({
                          ...prev,
                          options: { ...prev.options, restore_leave_requests: v }
                        }))}
                      />
                      <span className="text-sm">İzin Talepleri</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch 
                        checked={restoreDialog.options.restore_daily_statuses}
                        onCheckedChange={(v) => setRestoreDialog(prev => ({
                          ...prev,
                          options: { ...prev.options, restore_daily_statuses: v }
                        }))}
                      />
                      <span className="text-sm">Çalışma Durumları</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch 
                        checked={restoreDialog.options.restore_balances}
                        onCheckedChange={(v) => setRestoreDialog(prev => ({
                          ...prev,
                          options: { ...prev.options, restore_balances: v }
                        }))}
                      />
                      <span className="text-sm">İzin Bakiyeleri</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch 
                        checked={restoreDialog.options.restore_documents}
                        onCheckedChange={(v) => setRestoreDialog(prev => ({
                          ...prev,
                          options: { ...prev.options, restore_documents: v }
                        }))}
                      />
                      <span className="text-sm">Dokümanlar</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch 
                        checked={restoreDialog.options.restore_settings}
                        onCheckedChange={(v) => setRestoreDialog(prev => ({
                          ...prev,
                          options: { ...prev.options, restore_settings: v }
                        }))}
                      />
                      <span className="text-sm">Ayarlar</span>
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setRestoreDialog({ open: false, firm: null, loading: false, file: null, preview: null, options: {} })}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleRestore} 
              disabled={!restoreDialog.preview || restoreDialog.loading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {restoreDialog.loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Yükleniyor...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Geri Yükle
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog - Create Firm from Backup */}
      <Dialog open={importDialog.open} onOpenChange={(open) => !open && setImportDialog({ open: false, loading: false, file: null, preview: null, newFirmName: '' })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-600" />
              Yedekten Firma Oluştur
            </DialogTitle>
            <DialogDescription>
              JSON yedek dosyasından yeni firma ve tüm verileri oluşturun
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* File Upload */}
            <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg text-center">
              <input
                type="file"
                accept=".json"
                onChange={handleImportFileChange}
                className="hidden"
                id="import-file-input"
              />
              <label htmlFor="import-file-input" className="cursor-pointer">
                <FileJson className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                <p className="text-slate-600 dark:text-slate-300">Yedek dosyasını seçin (.json)</p>
                <p className="text-xs text-slate-400 mt-1">Dosyayı tıklayarak seçin</p>
              </label>
              {importDialog.file && (
                <div className="mt-3 p-2 bg-blue-50 rounded text-blue-700 text-sm">
                  <CheckCircle className="h-4 w-4 inline mr-1" />
                  {importDialog.file.name}
                </div>
              )}
            </div>
            
            {/* Preview */}
            {importDialog.preview && (
              <>
                {/* Source Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Building2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Kaynak Firma Bilgisi</p>
                      <p className="text-sm text-blue-700">
                        Firma: <strong>{importDialog.preview.firm?.name}</strong>
                      </p>
                      <p className="text-sm text-blue-700">
                        Yedek Tarihi: {new Date(importDialog.preview.backup_info?.created_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* New Firm Name */}
                <div className="space-y-2">
                  <Label>Yeni Firma Adı</Label>
                  <Input
                    value={importDialog.newFirmName}
                    onChange={(e) => setImportDialog(prev => ({ ...prev, newFirmName: e.target.value }))}
                    placeholder="Firma adını girin veya boş bırakarak orijinal adı kullanın"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">Boş bırakırsanız "{importDialog.preview.firm?.name} (Import)" olarak oluşturulur</p>
                </div>
                
                {/* Statistics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <Users className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                    <p className="text-2xl font-bold text-blue-700">{importDialog.preview.statistics?.users_count || 0}</p>
                    <p className="text-xs text-blue-600">Kullanıcı</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <Calendar className="h-5 w-5 mx-auto text-orange-600 mb-1" />
                    <p className="text-2xl font-bold text-orange-700">{importDialog.preview.statistics?.leave_requests_count || 0}</p>
                    <p className="text-xs text-orange-600">İzin Talebi</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <CheckCircle className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
                    <p className="text-2xl font-bold text-emerald-700">{importDialog.preview.statistics?.daily_statuses_count || 0}</p>
                    <p className="text-xs text-emerald-600">Çalışma Durumu</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <FileText className="h-5 w-5 mx-auto text-purple-600 mb-1" />
                    <p className="text-2xl font-bold text-purple-700">{importDialog.preview.statistics?.documents_count || 0}</p>
                    <p className="text-xs text-purple-600">Doküman</p>
                  </div>
                </div>

                {/* Users Preview */}
                {importDialog.preview.users?.length > 0 && (
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2">İçe Aktarılacak Kullanıcılar ({importDialog.preview.users.length})</h4>
                    <div className="max-h-32 overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {importDialog.preview.users.slice(0, 15).map(u => (
                          <span key={u.id} className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                            {u.full_name} ({u.email})
                          </span>
                        ))}
                        {importDialog.preview.users.length > 15 && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">+{importDialog.preview.users.length - 15} daha</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Info Box */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Önemli Bilgi</p>
                    <p>Tüm veriler yeni ID'lerle oluşturulacak. Email adresi zaten sistemde kayıtlı olan kullanıcılar atlanacaktır.</p>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setImportDialog({ open: false, loading: false, file: null, preview: null, newFirmName: '' })}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!importDialog.preview || importDialog.loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {importDialog.loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Firmayı Oluştur
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Firms;
